// §26.5 / §13 — a message you are not looking at reaches you anyway (D-338).
//
// The doorbell already worked: the server publishes a `chat` frame, the client re-`GET`s, and the
// Chat app redraws. What it could not do was tell you about a message when **the Chat window is
// shut**, because the only listener was the app — and a closed app has no listener. A chat that is
// silent unless you already have it open is a chat you have to keep checking, which is the thing
// notifications exist to stop.
//
// So this lives in the kernel, beside the alarm schedule and for the same reason: it has to work
// when the window does not exist. It listens to `social`, and on a `chat` frame it does what the
// app would have done — `GET /api/chat/threads` — and posts §13 for anything new.
//
// **It has to `GET`.** D-217 keeps every payload off the stream, so the frame says only that
// something changed. That is one round trip per message rather than none, and it is the price of
// having exactly one delivery path for a message body instead of two that can disagree.

import type { NotificationInput, Thread } from '@hangar/shared';

export interface ChatNotifierOptions {
  /** `GET /api/chat/threads` — the same call the app makes. */
  threads: () => Promise<Thread[]>;
  /**
   * §13. Takes the peer as well as the text (D-345): §13's own click behaviour focuses the window
   * that posted, and the poster here is the *shell* — so the caller has to remember which
   * conversation this was about if clicking it is ever to open one.
   */
  post: (input: NotificationInput, peer: string) => void;
  /**
   * Is the Chat window in front of the user right now?
   *
   * The one case that must stay silent: a message arriving in the thread somebody is reading is
   * already on their screen, and a toast for it is noise about something they can see.
   */
  chatIsFocused: () => boolean;
  signedIn: () => boolean;
  onError?: (error: unknown) => void;
}

export interface ChatNotifier {
  /** Called on every `chat` doorbell frame. Safe to call concurrently. */
  announce(): Promise<void>;
  /**
   * Forget what has been announced.
   *
   * Sign-out, or a sign-in as somebody else: the ids are another account's, and keeping them would
   * silence the first message of the new session.
   */
  reset(): void;
}

export function createChatNotifier(options: ChatNotifierOptions): ChatNotifier {
  const onError = options.onError ?? ((error: unknown) => console.warn('chat notify', error));
  /**
   * The newest message already announced, per peer.
   *
   * Keyed by peer rather than kept as a set of every id: a thread only ever announces its latest
   * message, so one id per conversation is all it takes to not say the same thing twice — and a
   * set that grew with the history would be a leak with a message's lifetime.
   */
  const announced = new Map<string, string>();
  /** True until the first `announce` has settled, so a cold start is not a burst of old messages. */
  let cold = true;

  return {
    async announce() {
      if (!options.signedIn()) return;
      let rows: Thread[];
      try {
        rows = await options.threads();
      } catch (error) {
        // A doorbell that could not be answered is not worth a toast about the doorbell.
        onError(error);
        return;
      }

      const focused = options.chatIsFocused();
      for (const thread of rows) {
        const last = thread.last;
        // Nothing to say about an empty thread, about a message this user sent, or about one that
        // has been deleted — a tombstone is the *absence* of a message (D-218).
        if (last === null || last.deleted || last.from !== thread.peer) continue;
        if (announced.get(thread.peer) === last.id) continue;
        announced.set(thread.peer, last.id);
        // The first pass only *learns* what is already there. Opening a laptop to six toasts for
        // messages read yesterday is worse than no notification at all.
        if (cold || focused) continue;
        options.post({ title: thread.displayName, body: last.body ?? '' }, thread.peer);
      }
      cold = false;
    },

    reset() {
      announced.clear();
      cold = true;
    },
  };
}
